import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment";
import Amount from "../../../ui-components/molecules/amount/amount";
import Tag from "../../../ui-components/molecules/tag/tag";
import { PaymentSummary } from "../../../app/data/payment/PaymentSession";
import { paymentChannelColor, paymentChannelLabel, statusColor } from "./paymentAdminUtils";

const columnHelper = createColumnHelper<PaymentSummary>();

export const PAYMENT_SEARCH_COLUMN_OPTIONS: { id: string; label: string }[] = [
  { id: "dateCreated", label: "Date" },
  { id: "accountNumber", label: "Account" },
  { id: "accountName", label: "Account name" },
  { id: "status", label: "Status" },
  { id: "paymentChannel", label: "Paid via" },
  { id: "paymentMethodLabel", label: "Payment type" },
  { id: "amountCents", label: "Invoice subtotal" },
  { id: "surchargeAmountCents", label: "Surcharge" },
  { id: "totalAmountPaid", label: "Total amount paid" },
  { id: "provider", label: "Provider" },
  { id: "invoiceNumbers", label: "Invoices" },
  { id: "paymentId", label: "Payment ID" },
];

export const PAYMENT_SEARCH_COLUMNS_HIDDEN_BY_DEFAULT: string[] = [];

const invoiceNumbersOf = (row: PaymentSummary): number[] => {
  if (row.invoiceNumbers && row.invoiceNumbers.length) return row.invoiceNumbers;
  return (row.invoices || []).map((line) => line.invoiceNumber);
};

const formatInvoiceCell = (row: PaymentSummary): React.ReactNode => {
  const numbers = invoiceNumbersOf(row);
  if (!numbers.length) return "—";
  if (numbers.length === 1) return String(numbers[0]);
  return (
    <span>
      {numbers[0]}
      <span className="xgs-payment-admin__invoice-extra">+{numbers.length - 1}</span>
    </span>
  );
};

const totalPaidCents = (row: PaymentSummary): number | null => {
  if (row.amountCents == null && row.surchargeAmountCents == null) return null;
  return (row.amountCents || 0) + (row.surchargeAmountCents || 0);
};

const moneyCell = (cents: number | null | undefined) =>
  cents == null ? "—" : <Amount amount={cents / 100} />;

const getPaymentSearchColumns = () => [
  columnHelper.accessor("dateCreated", {
    id: "dateCreated",
    header: "Date",
    cell: (info) => {
      const value = info.getValue();
      return value ? moment(value).format("MM/DD/YYYY h:mm A") : "—";
    },
    size: 180,
  }),
  columnHelper.accessor("accountNumber", {
    id: "accountNumber",
    header: "Account",
    cell: (info) => info.getValue() ?? "—",
    size: 110,
  }),
  columnHelper.accessor("accountName", {
    id: "accountName",
    header: "Account name",
    cell: (info) => info.getValue() || "—",
    size: 200,
  }),
  columnHelper.accessor("status", {
    id: "status",
    header: "Status",
    cell: (info) => (
      <Tag mods={{ color: statusColor(info.getValue()) }}>{info.getValue()}</Tag>
    ),
    size: 130,
  }),
  columnHelper.accessor("paymentChannel", {
    id: "paymentChannel",
    header: "Paid via",
    cell: (info) => {
      const channel = info.getValue();
      if (!channel) return "—";
      return <Tag mods={{ color: paymentChannelColor(channel) }}>{paymentChannelLabel(channel)}</Tag>;
    },
    size: 150,
  }),
  columnHelper.accessor("paymentMethodLabel", {
    id: "paymentMethodLabel",
    header: "Payment type",
    cell: (info) => info.getValue() || "—",
    size: 140,
  }),
  columnHelper.accessor("amountCents", {
    id: "amountCents",
    header: "Invoice subtotal",
    cell: (info) => moneyCell(info.getValue()),
    size: 150,
  }),
  columnHelper.accessor("surchargeAmountCents", {
    id: "surchargeAmountCents",
    header: "Surcharge",
    cell: (info) => moneyCell(info.getValue()),
    size: 130,
  }),
  columnHelper.accessor((row) => totalPaidCents(row), {
    id: "totalAmountPaid",
    header: "Total amount paid",
    cell: (info) => moneyCell(info.getValue()),
    size: 160,
  }),
  columnHelper.accessor("provider", {
    id: "provider",
    header: "Provider",
    cell: (info) => info.getValue() || "—",
    size: 150,
  }),
  columnHelper.accessor((row) => invoiceNumbersOf(row).join(", "), {
    id: "invoiceNumbers",
    header: "Invoices",
    cell: (info) => formatInvoiceCell(info.row.original),
    size: 150,
  }),
  columnHelper.accessor("paymentId", {
    id: "paymentId",
    header: "Payment ID",
    cell: (info) => <span className="xgs-payment-admin__id">{info.getValue()}</span>,
    size: 280,
  }),
];

export default getPaymentSearchColumns;
